import React, { useState, useEffect, useCallback, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Dashboard.jsx
 * npm install recharts   (if not already in the project)
 *
 * Pulls aggregate numbers from dashboard_stats.php. This endpoint is
 * intentionally NOT privilege-filtered -- everyone who opens the
 * dashboard sees company-wide totals, unlike the enquiry table itself.
 */

const STATUS_COLORS = {
  Open: "#0284c7",
  "Follow-up": "#d97706",
  Quoted: "#7c3aed",
  Won: "#059669",
  Lost: "#e11d48",
};
const FALLBACK_COLOR = "#64748b";

const EMPTY_STATS = {
  total: 0,
  won: 0,
  lost: 0,
  pipeline: 0,
  open: 0,
  won_percent: 0,
  lost_percent: 0,
  status_breakdown: [],
  monthly_trend: [],
};

function StatCard({ label, value, accent, sub }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        <span className={`w-2.5 h-2.5 rounded-full ${accent}`} />
      </div>
      <p className="text-3xl font-semibold text-slate-900 mt-2">{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-5">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      {subtitle && <p className="text-xs text-slate-400 mt-0.5 mb-3">{subtitle}</p>}
      <div className="mt-2">{children}</div>
    </div>
  );
}

export default function Dashboard() {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${SERVER_URL}/Rahul/dashboard_stats.php`);
      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Failed to load dashboard data");
      setStats({ ...EMPTY_STATS, ...data.data });
    } catch (err) {
      setError(err.message || "Something went wrong while loading the dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const pieData = useMemo(
    () => stats.status_breakdown.map((s) => ({ name: s.status, value: s.count })),
    [stats.status_breakdown]
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Page header */}
      <div className="bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white hover:bg-slate-700 p-2 rounded-lg"
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-white tracking-tight">Dashboard</h1>
            <p className="text-sm text-slate-400 mt-1">
              Company-wide enquiry performance, across all employees
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-sm px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center text-slate-400 py-16">Loading dashboard…</div>
        ) : (
          <>
            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Total Enquiries" value={stats.total} accent="bg-indigo-500" />
              <StatCard
                label="Won"
                value={stats.won}
                accent="bg-emerald-500"
                sub={`${stats.won_percent}% of total`}
              />
              <StatCard
                label="Lost"
                value={stats.lost}
                accent="bg-rose-500"
                sub={`${stats.lost_percent}% of total`}
              />
              <StatCard
                label="In Pipeline"
                value={stats.pipeline}
                accent="bg-amber-500"
                sub="Follow-up + Quoted"
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
              <ChartCard title="Enquiries by Status" subtitle="Current distribution across every status">
                {pieData.length === 0 ? (
                  <p className="text-sm text-slate-400 py-10 text-center">No data yet</p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={60}
                        outerRadius={95}
                        paddingAngle={2}
                      >
                        {pieData.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={STATUS_COLORS[entry.name] || FALLBACK_COLOR}
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend verticalAlign="bottom" height={36} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>

              <ChartCard
                title="Generated vs Won"
                subtitle="Monthly totals, last 12 months (by enquiry date)"
              >
                {stats.monthly_trend.length === 0 ? (
                  <p className="text-sm text-slate-400 py-10 text-center">No data yet</p>
                ) : (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={stats.monthly_trend} margin={{ left: -10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="generated"
                        name="Generated"
                        stroke="#6366f1"
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="won"
                        name="Won"
                        stroke="#10b981"
                        strokeWidth={2.5}
                        dot={{ r: 3 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </ChartCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
}